document.addEventListener('DOMContentLoaded', () => {
    // Cart discount-code field. Submits to Shopify's documented
    // /discount/{CODE}?redirect=/cart route, which persists the code on the
    // cart and carries it into checkout. The input lives inside the cart's
    // <form>, so Enter would otherwise trigger a cart-update POST — we
    // intercept and redirect to the discount URL instead.
    //
    // Silent-failure guard: Shopify redirects back to /cart with no signal
    // when the code is invalid. We stash the attempted code in sessionStorage
    // before navigating, then on cart-page load (below) compare it against
    // the <span data-cart-applied-discounts> marker rendered by main-cart.liquid.
    // Match → success toast; no match → "code isn't valid" toast.
    const discountApply = document.querySelector('[data-cart-discount-apply]');
    const discountInput = document.querySelector('[data-cart-discount-input]');
    if (discountApply && discountInput) {
        const applyDiscount = () => {
            const code = discountInput.value.trim();
            if (!code) {
                discountInput.focus();
                return;
            }
            try { sessionStorage.setItem('kriya:pending-discount', code); } catch (e) {}
            // Shopify accepts only [A-Za-z0-9_-] in discount codes; encodeURIComponent
            // is defensive in case a merchant chose a code with reserved chars.
            window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/cart';
        };
        discountApply.addEventListener('click', applyDiscount);
        discountInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                applyDiscount();
            }
        });
    }

    // Hide the cart sticky-mobile bar while the discount-code panel is open
    // so the iOS keyboard pushing the page up can't land the sticky bar on
    // top of the input. CSS uses :has() for modern browsers; this JS-class
    // shim covers iOS 15.0–15.3 (no :has() support) by setting
    // .is-discount-open on .cart-page, which the parallel CSS selector
    // (cart-page.is-discount-open .cart-sticky-mobile) targets.
    const discountDetails = document.querySelector('[data-cart-discount]');
    const cartPage = document.querySelector('.cart-page');
    if (discountDetails && cartPage) {
        discountDetails.addEventListener('toggle', () => {
            cartPage.classList.toggle('is-discount-open', discountDetails.open);
        });
    }

    // Discount-apply feedback on cart load. Runs only when the marker span
    // is present (i.e. we're on the cart page). Compares the pending code
    // from sessionStorage against the CSV of applied codes Shopify rendered.
    const appliedMarker = document.querySelector('[data-cart-applied-discounts]');
    if (appliedMarker) {
        let pending = null;
        try { pending = sessionStorage.getItem('kriya:pending-discount'); } catch (e) {}
        if (pending) {
            try { sessionStorage.removeItem('kriya:pending-discount'); } catch (e) {}
            const applied = (appliedMarker.getAttribute('data-codes') || '')
                .toLowerCase()
                .split(',')
                .filter(Boolean);
            // Defer to next tick so showToast (defined further down in this
            // file's DOMContentLoaded scope) has been hoisted.
            requestAnimationFrame(() => {
                if (typeof showToast !== 'function') return;
                if (applied.includes(pending.toLowerCase())) {
                    showToast('Discount applied: ' + pending.toUpperCase(), 'success', { hideCta: true });
                } else {
                    showToast('Code "' + pending + '" isn\'t valid', 'error', { hideCta: true });
                }
            });
        }
    }

    // Theme toggle. The FOUC script in theme.liquid head has already set
    // <html data-theme="light|dark"> before this runs, so we just sync the
    // button's aria-pressed to the current state and bind the click handler.
    // Persists the user's choice to localStorage so they don't have to
    // re-toggle on every page load.
    const themeToggle = document.querySelector('[data-theme-toggle]');
    if (themeToggle) {
        const root = document.documentElement;
        const syncPressed = () => {
            themeToggle.setAttribute('aria-pressed', root.getAttribute('data-theme') === 'dark');
        };
        syncPressed();
        themeToggle.addEventListener('click', () => {
            const next = root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
            root.setAttribute('data-theme', next);
            try { localStorage.setItem('kriya:theme', next); } catch (e) {}
            syncPressed();
        });
    }

    // Mobile Menu Toggle — class-based, brand-token styled, fully accessible.
    // Adds: focus trap inside the open panel, focus return on close, body
    // scroll lock, and a backdrop scrim. Backdrop is created lazily on first
    // open so non-mobile users never get the extra node.
    const hamburger = document.querySelector('.hamburger');
    const navLinks = document.querySelector('.nav-links');
    let backdrop = null;
    let lastFocus = null;

    function ensureBackdrop() {
        if (backdrop) return backdrop;
        backdrop = document.createElement('div');
        backdrop.className = 'nav-backdrop';
        backdrop.setAttribute('aria-hidden', 'true');
        backdrop.addEventListener('click', closeNav);
        document.body.appendChild(backdrop);
        return backdrop;
    }

    function focusableInNav() {
        if (!navLinks) return [];
        return Array.from(navLinks.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])'))
            .filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
    }

    function trapTab(e) {
        if (e.key !== 'Tab') return;
        const items = focusableInNav();
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }

    function openNav() {
        lastFocus = document.activeElement;
        hamburger.classList.add('is-open');
        navLinks.classList.add('nav-open');
        hamburger.setAttribute('aria-expanded', 'true');
        hamburger.setAttribute('aria-label', 'Close menu');
        document.body.classList.add('is-nav-open');
        ensureBackdrop().classList.add('is-visible');
        // Move focus into the panel so keyboard / screen-reader users land
        // in the menu, not back on the page below.
        const first = focusableInNav()[0];
        if (first) first.focus();
        navLinks.addEventListener('keydown', trapTab);
    }

    function closeNav() {
        if (!hamburger || !navLinks) return;
        hamburger.classList.remove('is-open');
        navLinks.classList.remove('nav-open');
        hamburger.setAttribute('aria-expanded', 'false');
        hamburger.setAttribute('aria-label', 'Open menu');
        document.body.classList.remove('is-nav-open');
        if (backdrop) backdrop.classList.remove('is-visible');
        navLinks.removeEventListener('keydown', trapTab);
        // Return focus to whatever opened the drawer (typically .hamburger).
        // Guard against document.body — on a fresh page load with touch-open,
        // activeElement is <body>, which is technically focusable but lands
        // focus invisibly. Fall through to .hamburger in that case so keyboard
        // users see a visible focus ring on something meaningful.
        if (lastFocus && lastFocus !== document.body && typeof lastFocus.focus === 'function') {
            lastFocus.focus();
        } else if (hamburger) {
            hamburger.focus();
        }
    }

    if (hamburger && navLinks) {
        hamburger.addEventListener('click', () => {
            hamburger.getAttribute('aria-expanded') === 'true' ? closeNav() : openNav();
        });

        // Close on outside click (backdrop handles this on mobile; this keeps
        // the same behavior on viewports where the backdrop isn't rendered).
        document.addEventListener('click', (e) => {
            if (!hamburger.contains(e.target) && !navLinks.contains(e.target)) {
                if (hamburger.getAttribute('aria-expanded') === 'true') closeNav();
            }
        });

        // Close on Esc
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && hamburger.getAttribute('aria-expanded') === 'true') closeNav();
        });

        // Close when any nav link is tapped (mobile)
        navLinks.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', closeNav);
        });
    }

    // Smooth Scroll for #anchor links.
    // Skip-link and any opt-out are excluded so the browser's native focus
    // behaviour (move focus to the target so keyboard/SR users land there)
    // is preserved.
    document.querySelectorAll('a[href^="#"]:not(.skip-link):not([data-no-smooth-scroll])').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            const hash = this.getAttribute('href');
            if (!hash || hash === '#') return;
            const target = document.querySelector(hash);
            if (!target) return;
            e.preventDefault();
            target.scrollIntoView({ behavior: 'smooth' });
            // Move focus too so screen-reader and keyboard users land on the
            // anchored region rather than staying on the link.
            const prevTabIndex = target.getAttribute('tabindex');
            if (prevTabIndex === null) target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
            if (prevTabIndex === null) {
                target.addEventListener('blur', function once() {
                    target.removeAttribute('tabindex');
                    target.removeEventListener('blur', once);
                });
            }
        });
    });

    // Launch-date countdown — single shared implementation for every
    // [data-launch] button on any page (was previously duplicated in
    // featured-collection, main-collection, and main-product sections).
    function initLaunchCountdowns() {
        const buttons = document.querySelectorAll('[data-launch]');
        const cartIconSvg = `<svg class="cart-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M3 3H5L5.4 5M5.4 5H21L17 13H7M5.4 5L7 13M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H17M17 16.5C16.2 16.5 15.5 17.2 15.5 18S16.2 19.5 17 19.5 18.5 18.8 18.5 18 17.8 16.5 17 16.5ZM9 18C9 18.8 8.3 19.5 7.5 19.5S6 18.8 6 18S6.7 16.5 7.5 16.5 9 17.2 9 18Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

        buttons.forEach(button => {
            const launchDate = new Date(button.dataset.launch);
            const isAvailable = button.dataset.available === 'true';
            const countdownSpan = button.querySelector('.countdown-text');
            const isProductButton = button.classList.contains('btn-large') || button.classList.contains('sticky-buy-btn');
            let intervalId = null;

            function update() {
                const diff = launchDate - new Date();
                if (diff <= 0) {
                    clearInterval(intervalId);
                    button.classList.remove('btn-coming-soon');
                    if (isAvailable) {
                        button.disabled = false;
                        button.removeAttribute('aria-disabled');
                        if (isProductButton) {
                            button.innerHTML = cartIconSvg + ' Add to cart';
                        } else {
                            button.type = 'submit';
                            button.textContent = 'Add to cart';
                        }
                    } else {
                        button.classList.add('btn-out-of-stock');
                        button.setAttribute('aria-disabled', 'true');
                        button.textContent = 'Out of stock';
                    }
                    return;
                }
                const days    = Math.floor(diff / 86400000);
                const hours   = Math.floor((diff % 86400000) / 3600000);
                const minutes = Math.floor((diff % 3600000) / 60000);
                const text = `Launching in ${days}D:${hours}H:${minutes}M`;
                if (countdownSpan) {
                    countdownSpan.textContent = text;
                } else {
                    button.textContent = text;
                }
            }
            update();
            intervalId = setInterval(update, 60000);
        });
    }
    initLaunchCountdowns();

    // Password show/hide — auto-wires a 44×44 eye-icon toggle next to every
    // <input type="password"> inside .login-card. Critical on mobile where
    // typo rates are high, especially on Activate / Reset (double-entry).
    (function initPasswordToggles() {
        const inputs = document.querySelectorAll('.login-card input[type="password"]');
        inputs.forEach((input) => {
            if (input.dataset.pwToggleBound) return;
            input.dataset.pwToggleBound = '1';
            // Wrap input in .password-field for absolute positioning of the
            // toggle button. Preserves label[for] association — the input
            // moves with the same id, no markup change.
            const wrap = document.createElement('div');
            wrap.className = 'password-field';
            input.parentNode.insertBefore(wrap, input);
            wrap.appendChild(input);

            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'password-toggle';
            btn.setAttribute('aria-pressed', 'false');
            btn.setAttribute('aria-label', 'Show password');
            // SVG eye + eye-slash, swapped via CSS on aria-pressed.
            btn.innerHTML = `
                <svg class="icon-show" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg class="icon-hide" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.69 19.69 0 0 1 5.06-5.94"/><path d="M9.9 4.24A10.95 10.95 0 0 1 12 4c7 0 11 8 11 8a19.65 19.65 0 0 1-3.4 4.46"/><path d="M14.12 14.12A3 3 0 1 1 9.88 9.88"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            `;
            wrap.appendChild(btn);

            btn.addEventListener('click', () => {
                const showing = btn.getAttribute('aria-pressed') === 'true';
                const next = !showing;
                input.type = next ? 'text' : 'password';
                btn.setAttribute('aria-pressed', next ? 'true' : 'false');
                btn.setAttribute('aria-label', next ? 'Hide password' : 'Show password');
            });
        });
    })();

    // AJAX cart-add — converts the silent page-reload pattern into a
    // toast confirmation + live cart-count update in the navbar.
    // Falls back to native form submit if fetch fails or anything is amiss.
    function getCartLink() {
        return document.querySelector('a.btn-nav[href$="/cart"]');
    }

    function updateCartCount(count) {
        const label = count === 0
            ? 'Cart'
            : `Cart, ${count} item${count === 1 ? '' : 's'}`;

        // Desktop text link inside .nav-links.
        const link = getCartLink();
        if (link) {
            // Suppress "(0)" on an empty cart — matches header.liquid render.
            link.textContent = count > 0 ? `Cart (${count})` : 'Cart';
            link.setAttribute('aria-label', label);
        }

        // Mobile navbar icon button — keep the SVG, update the badge only.
        const mobileLink = document.querySelector('.nav-cart-mobile');
        if (mobileLink) mobileLink.setAttribute('aria-label', label);
        const badge = document.querySelector('.nav-cart-mobile [data-cart-count]');
        if (badge) {
            const prev = parseInt(badge.textContent, 10) || 0;
            if (count > 0) {
                badge.textContent = count;
                badge.hidden = false;
                // Bump-scale animation on every increase so the user sees
                // the badge update without staring at it. No bump on
                // decrease (remove-item) — that has its own toast.
                if (count > prev) {
                    badge.classList.remove('is-bumped');
                    // Force reflow so the keyframe restarts even when
                    // consecutive adds fire quickly.
                    void badge.offsetWidth;
                    badge.classList.add('is-bumped');
                }
            } else {
                badge.textContent = '';
                badge.hidden = true;
            }
        }

        // SR announcement so blind users hear cart changes. Pause + replace
        // the text so consecutive updates each emit (live regions don't
        // re-announce if the text is identical).
        const live = document.getElementById('cart-live-region');
        if (live) {
            live.textContent = '';
            // Microtask delay so the empty-string clear registers before
            // the new message — required for the AT to re-announce.
            requestAnimationFrame(() => { live.textContent = `Cart updated. ${label}.`; });
        }
    }

    function showToast(message, variant, opts) {
        const o = opts || {};
        const existing = document.querySelector('.kriya-toast');
        if (existing) existing.remove();
        const toast = document.createElement('div');
        toast.className = `kriya-toast kriya-toast--${variant || 'success'}`;
        // Errors are alerts (assertive — SR interrupts current speech);
        // success is a polite status update. Without this distinction,
        // SR users could miss a failed cart-add behind ongoing speech.
        if (variant === 'error') {
            toast.setAttribute('role', 'alert');
            toast.setAttribute('aria-live', 'assertive');
        } else {
            toast.setAttribute('role', 'status');
            toast.setAttribute('aria-live', 'polite');
        }
        // The "View cart →" CTA is redundant when the toast fires ON the cart
        // page (e.g. discount-code feedback) — caller passes hideCta:true to
        // suppress it. Default behavior keeps the link for add-to-cart toasts
        // fired from PDP/collection pages.
        const ctaHtml = o.hideCta ? '' : '<a href="/cart" class="kriya-toast__cta">View cart →</a>';
        toast.innerHTML = `
            <span class="kriya-toast__msg">${message}</span>
            ${ctaHtml}
            <button type="button" class="kriya-toast__close" aria-label="Dismiss notification">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg>
            </button>
        `;
        document.body.appendChild(toast);
        requestAnimationFrame(() => toast.classList.add('is-visible'));

        // Auto-dismiss after 4s, but pause the timer while the toast is
        // hovered/focused — gives users time to read or click "View cart"
        // without racing the clock. Resumes when pointer/focus leaves.
        let dismissTimer = null;
        const startDismissTimer = (ms) => {
            window.clearTimeout(dismissTimer);
            dismissTimer = window.setTimeout(() => {
                toast.classList.remove('is-visible');
                window.setTimeout(() => toast.remove(), 300);
            }, ms);
        };
        const cancelDismissTimer = () => window.clearTimeout(dismissTimer);
        startDismissTimer(4000);

        toast.addEventListener('mouseenter', cancelDismissTimer);
        toast.addEventListener('focusin', cancelDismissTimer);
        toast.addEventListener('mouseleave', () => startDismissTimer(2000));
        toast.addEventListener('focusout', (e) => {
            // Restart only when focus leaves the toast entirely, not when
            // it moves between the message link and close button.
            if (!toast.contains(e.relatedTarget)) startDismissTimer(2000);
        });

        const closeBtn = toast.querySelector('.kriya-toast__close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                cancelDismissTimer();
                toast.classList.remove('is-visible');
                window.setTimeout(() => toast.remove(), 300);
            });
        }
    }

    function attachCartAjax(form) {
        if (form.dataset.cartAjaxBound) return;
        form.dataset.cartAjaxBound = '1';

        form.addEventListener('submit', async (e) => {
            // e.submitter is the button the user actually pressed (PDP has two
            // submits — desktop .btn-large and the mobile sticky CTA). Falling
            // back to the first submit only covers cases where the form is
            // submitted programmatically.
            const submitBtn = e.submitter || form.querySelector('button[type="submit"]');
            if (!submitBtn || submitBtn.disabled) return;
            e.preventDefault();
            const originalLabel = submitBtn.innerHTML;
            submitBtn.disabled = true;
            submitBtn.innerHTML = 'Adding…';

            try {
                const formData = new FormData(form);
                const addResponse = await fetch('/cart/add.js', {
                    method: 'POST',
                    body: formData,
                    headers: { 'Accept': 'application/json' }
                });
                if (!addResponse.ok) throw new Error('add failed');
                const added = await addResponse.json();

                const cartResponse = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
                if (cartResponse.ok) {
                    const cart = await cartResponse.json();
                    updateCartCount(cart.item_count);
                }

                const productTitle = added.product_title || 'Item';
                showToast(`✓ ${productTitle} added to cart`, 'success');
                submitBtn.innerHTML = '✓ Added';
                setTimeout(() => {
                    submitBtn.disabled = false;
                    submitBtn.innerHTML = originalLabel;
                }, 1500);
            } catch (err) {
                submitBtn.disabled = false;
                submitBtn.innerHTML = originalLabel;
                showToast('Could not add to cart. Please try again.', 'error');
            }
        });
    }

    document.querySelectorAll(`form[action$="/cart/add"]`).forEach(attachCartAjax);

    // Generic autofocus — `autofocus` HTML attribute is unreliable on iOS
    // Safari from a cold page load. JS focus inside requestAnimationFrame
    // works around it. Opt in per input via data-autofocus.
    (function initAutofocus() {
        const target = document.querySelector('[data-autofocus]');
        if (!target) return;
        requestAnimationFrame(() => {
            try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); }
        });
    })();

    // Generic submit-busy — disables the submit button and swaps its label
    // to a "Sending…" / custom string while the form is posting. Prevents
    // double-submits on slow connections (newsletter, contact, addresses).
    // Opt in per form via data-submit-busy="Saving…" attribute. The form
    // hands off control to the native submit; this handler only manages
    // visual state.
    (function initSubmitBusy() {
        document.querySelectorAll('form[data-submit-busy]').forEach((form) => {
            if (form.dataset.submitBusyBound) return;
            form.dataset.submitBusyBound = '1';
            form.addEventListener('submit', () => {
                const btn = form.querySelector('button[type="submit"], input[type="submit"]');
                if (!btn) return;
                const busyLabel = form.dataset.submitBusy || 'Sending…';
                btn.dataset.originalLabel = btn.dataset.originalLabel || btn.innerHTML || btn.value || '';
                btn.disabled = true;
                if (btn.tagName === 'BUTTON') btn.innerHTML = busyLabel;
                else btn.value = busyLabel;
            });
        });
    })();

    // Cart qty stepper — wires +/− buttons on each cart line item to
    // POST /cart/change.js for instant updates without a page reload.
    // Falls back to the existing "Update cart" form submit if fetch fails.
    // The line index is 1-based per Shopify's API contract.
    (function initCartQtySteppers() {
        const steppers = document.querySelectorAll('[data-cart-qty]');
        if (!steppers.length) return;

        const moneyFormat = (cents) => {
            try {
                return new Intl.NumberFormat('en-IN', {
                    style: 'currency', currency: 'INR', maximumFractionDigits: 0
                }).format(cents / 100);
            } catch (_) {
                return '₹' + (cents / 100).toFixed(0);
            }
        };

        const updateSubtotalUI = (cart) => {
            // Top-level subtotal in the order summary.
            const sub = document.querySelector('.subtotal-amount');
            if (sub) sub.textContent = moneyFormat(cart.total_price);
            // Mobile sticky bar amount.
            const stickyAmt = document.querySelector('.cart-sticky-mobile-amount');
            if (stickyAmt) stickyAmt.textContent = moneyFormat(cart.total_price);
            // Mobile sticky bar item count (label and Checkout button).
            const itemCountStr = String(cart.item_count);
            const itemCountLabel = document.querySelector('[data-sticky-item-count]');
            if (itemCountLabel) itemCountLabel.textContent = itemCountStr;
            const checkoutCount = document.querySelector('[data-sticky-checkout-count]');
            if (checkoutCount) checkoutCount.textContent = itemCountStr;
            const stickyLabel = document.querySelector('.cart-sticky-mobile-label');
            if (stickyLabel) {
                // Recompose so "item/items" pluralization stays in sync.
                stickyLabel.innerHTML = `Subtotal · <span data-sticky-item-count>${itemCountStr}</span> item${cart.item_count === 1 ? '' : 's'}`;
            }
            // Cart count in nav (desktop text + mobile badge).
            updateCartCount(cart.item_count);
            // Page H1 "Your cart (N items)".
            const h1 = document.querySelector('.cart-intro .section-title');
            if (h1) {
                h1.textContent = cart.item_count > 0
                    ? `Your cart (${cart.item_count} item${cart.item_count === 1 ? '' : 's'})`
                    : 'Your cart';
            }
        };

        const updateLinePriceUI = (stepper, line) => {
            const row = stepper.closest('.cart-item');
            if (!row) return;
            const priceEl = row.querySelector('[data-line-price]');
            if (priceEl && line) {
                const formatted = moneyFormat(line.final_line_price);
                priceEl.textContent = formatted;
                // Keep aria-label in sync so SR users hear the updated total
                // (the cart-header is display:none at ≤600px, so the label is
                // the only column context on mobile).
                priceEl.setAttribute('aria-label', `Line total: ${formatted}`);
            }
        };

        const setBusy = (stepper, busy) => {
            stepper.setAttribute('aria-busy', busy ? 'true' : 'false');
            stepper.querySelectorAll('button').forEach((b) => b.disabled = busy);
        };

        // /cart/update.js + line keys avoids the stale-index problem that
        // /cart/change.js + line numbers would have after a remove (Shopify
        // re-numbers remaining lines, which our DOM doesn't see).
        const updateLine = async (stepper, nextQty) => {
            const lineKey = stepper.dataset.lineKey;
            if (!lineKey) return;
            setBusy(stepper, true);
            try {
                const resp = await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({ updates: { [lineKey]: nextQty } })
                });
                if (!resp.ok) {
                    // Shopify 422 → JSON body with `{status, message, description}`.
                    // Pull a context-rich message so the user knows *why* (out of
                    // stock vs. cap reached) rather than a generic retry prompt.
                    let serverMsg = null;
                    try {
                        const errBody = await resp.json();
                        serverMsg = errBody.description || errBody.message || null;
                    } catch (e) {}
                    const err = new Error(serverMsg || ('cart/update failed (' + resp.status + ')'));
                    err.isShopifyError = !!serverMsg;
                    throw err;
                }
                const cart = await resp.json();
                if (nextQty === 0) {
                    const row = stepper.closest('.cart-item');
                    if (row) row.remove();
                    if (cart.item_count === 0) {
                        // Empty cart needs the server-rendered empty-state snippet.
                        window.location.reload();
                        return;
                    }
                } else {
                    // Match the returned line by key (index-safe after removes).
                    const line = cart.items.find((it) => it.key === lineKey);
                    if (line) updateLinePriceUI(stepper, line);
                }
                updateSubtotalUI(cart);
            } catch (e) {
                // Fallback: revert the input value and surface a toast so
                // the user sees their tap "undid itself" was deliberate,
                // not a frozen UI.
                const input = stepper.querySelector('input[type="number"]');
                if (input) input.value = input.defaultValue;
                // Three error tiers:
                //   1. Shopify 422 (inventory cap, sold out) — pass its message through
                //   2. Network failure (no response at all) — connection prompt
                //   3. Anything else (5xx, JSON parse fail, etc.) — generic retry
                let msg;
                if (e.isShopifyError) {
                    msg = e.message;
                } else if (e instanceof TypeError) {
                    // fetch() throws TypeError on offline / DNS / CORS / abort
                    msg = "Can't reach the store — check your connection";
                } else {
                    msg = "Couldn't update quantity — try again";
                }
                showToast(msg, 'error', { hideCta: true });
            } finally {
                setBusy(stepper, false);
            }
        };

        steppers.forEach((stepper) => {
            const input = stepper.querySelector('input[type="number"]');
            if (!input) return;
            // Save the rendered value as the source-of-truth fallback.
            input.defaultValue = input.value;
            const min = parseInt(input.getAttribute('min') || '0', 10);
            const maxAttr = parseInt(input.getAttribute('max'), 10);
            const max = isFinite(maxAttr) ? maxAttr : Infinity;

            stepper.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-qty-action]');
                if (!btn) return;
                const action = btn.getAttribute('data-qty-action');
                const current = parseInt(input.value, 10) || min;
                const next = action === 'decrement' ? current - 1 : current + 1;
                if (next < min || next > max) return;
                input.value = next;
                updateLine(stepper, next);
            });

            // Manual input edits also fire AJAX. Clamp to [min, max] so a
            // paste of 99999 doesn't silently submit and get capped upstream
            // (which would feel like a frozen UI on slow connections).
            input.addEventListener('change', () => {
                const raw = parseInt(input.value, 10) || min;
                const next = Math.min(max, Math.max(min, raw));
                input.value = next;
                updateLine(stepper, next);
            });
        });
    })();

    // Fade-up reveal on scroll for home sections
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealTargets = document.querySelectorAll('.features, .kits, .steps, .included, .testimonials-section, .faq-section, .final-cta, .kit-selector-closing');
    if (revealTargets.length && !reduceMotion && 'IntersectionObserver' in window) {
        revealTargets.forEach(el => el.classList.add('reveal-on-scroll'));
        const io = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    io.unobserve(entry.target);
                }
            });
        }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
        revealTargets.forEach(el => io.observe(el));
    }

    // Mobile sticky CTA — visible only when neither the hero nor the
    // final-CTA section is on screen. Two observers feed a combined
    // visibility state so the bar:
    //   1. stays hidden while the hero is in view (hero already shows a
    //      primary CTA — no need for the sticky bar to double up)
    //   2. reveals as the user scrolls past the hero into the body
    //   3. hides again when the final-CTA enters view so the page bottom
    //      doesn't show two near-identical orange primaries stacked
    //      (final-cta "Pick your kit" sitting directly above sticky
    //      "Get your kit") — that pile reads as a UI bug at the
    //      conversion moment.
    const stickyCta = document.querySelector('.mobile-cta-bar');
    const hero = document.querySelector('.hero');
    if (stickyCta && hero && 'IntersectionObserver' in window) {
        const finalCta = document.querySelector('.final-cta');
        let heroInView = true;        // starts hidden behind hero
        let finalCtaInView = false;

        function syncStickyCta() {
            const hidden = heroInView || finalCtaInView;
            stickyCta.classList.toggle('is-visible', !hidden);
            stickyCta.setAttribute('aria-hidden', String(hidden));
            // inert removes the bar's link from the keyboard tab order while
            // it's offscreen, so users tabbing through the hero don't land on
            // a button they can't see. aria-hidden alone doesn't block focus.
            if (hidden) {
                stickyCta.setAttribute('inert', '');
            } else {
                stickyCta.removeAttribute('inert');
            }
        }

        const heroIo = new IntersectionObserver(([entry]) => {
            heroInView = entry.isIntersecting;
            syncStickyCta();
        }, { threshold: 0, rootMargin: '0px 0px -60px 0px' });
        heroIo.observe(hero);

        if (finalCta) {
            const finalCtaIo = new IntersectionObserver(([entry]) => {
                finalCtaInView = entry.isIntersecting;
                syncStickyCta();
            }, { threshold: 0, rootMargin: '0px 0px -60px 0px' });
            finalCtaIo.observe(finalCta);
        }
    }

    /* PDP section order is now driven entirely by product.json
       (`order: ["main", "concepts"]`) — concepts always renders after
       the main product section on every viewport. The previous
       viewport-conditional reorder JS (initPdpMobileOrder) lived here
       to put concepts BEFORE main on desktop while keeping it AFTER
       on mobile; it was removed once the design settled on
       concepts-after-main everywhere. */
});
